import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Chip,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TablePagination,
  Card,
  CardContent,
  InputAdornment,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  MenuBook,
  Refresh,
  Search,
  FilterList,
  Clear,
} from '@mui/icons-material';
import Layout from '../components/Layout';
import SubjectVersionFilter from '../components/SubjectVersionFilter';
import FilterChips from '../components/FilterChips';
import { useAuth } from '../contexts/AuthContext';
import { curriculumAPI } from '../services/api';
import { Chapter, Subject, SubjectVersion } from '../types';

const ChapterManagePage: React.FC = () => {
  const { isSuperAdmin } = useAuth();
  const [chapters, setChapters] = useState<Chapter[]>([]);
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [subjectVersions, setSubjectVersions] = useState<SubjectVersion[]>([]);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingChapter, setEditingChapter] = useState<Chapter | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    orderIndex: 1,
    subjectVersionId: 0,
  });
  const [selectedSubjectId, setSelectedSubjectId] = useState<number>(0);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // 分页状态
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // 筛选和搜索状态
  const [searchTerm, setSearchTerm] = useState('');
  const [filterSubjectId, setFilterSubjectId] = useState<number>(0);
  const [filterVersionId, setFilterVersionId] = useState<number>(0);
  const [filterSchoolLevel, setFilterSchoolLevel] = useState<string>('');

  // 加载章节列表
  const loadChapters = async () => {
    try {
      setLoading(true);
      const data = await curriculumAPI.getChapters();
      setChapters(data);
    } catch (error: any) {
      console.error('加载章节列表失败:', error);
      setError('加载章节列表失败: ' + (error.message || '未知错误'));
    } finally {
      setLoading(false);
    }
  };

  // 加载学科列表
  const loadSubjects = async () => {
    try {
      const data = await curriculumAPI.getSubjects();
      setSubjects(data);
    } catch (error: any) {
      console.error('加载学科列表失败:', error);
    }
  };

  // 加载学科版本
  const loadSubjectVersions = async (subjectId: number) => {
    try {
      const data = await curriculumAPI.getSubjectVersions(subjectId);
      setSubjectVersions(data);
    } catch (error: any) {
      console.error('加载学科版本失败:', error);
    }
  };

  useEffect(() => {
    loadChapters();
    loadSubjects();
  }, []);

  // 获取章节的年级学期信息
  const getChapterSchoolLevel = useCallback((chapter: Chapter) => {
    // 首先尝试从章节DTO的 schoolLevel 字段获取（后端已经填充）
    if (chapter.schoolLevel) {
      return chapter.schoolLevel;
    }

    // 如果没有，尝试从关联的学科版本获取
    const subject = subjects.find(s =>
      s.versions?.some(v => v.id === chapter.subjectVersionId)
    );

    if (subject) {
      const version = subject.versions?.find(v => v.id === chapter.subjectVersionId);
      if (version?.schoolLevel) {
        return version.schoolLevel;
      }
    }

    return '未知年级';
  }, [subjects]);

  // 从实际数据中获取可用的年级学期选项
  const availableSchoolLevels = useMemo(() => {
    const levels = new Set<string>();
    chapters.forEach(chapter => {
      const schoolLevel = getChapterSchoolLevel(chapter);
      if (schoolLevel && schoolLevel !== '未知年级') {
        levels.add(schoolLevel);
      }
    });
    return Array.from(levels).sort();
  }, [chapters, getChapterSchoolLevel]);

  // 筛选和搜索逻辑
  const filteredChapters = useMemo(() => {
    let filtered = chapters;

    // 搜索过滤
    if (searchTerm) {
      filtered = filtered.filter(chapter =>
        chapter.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // 学科过滤
    if (filterSubjectId > 0) {
      filtered = filtered.filter(chapter => {
        const subjectVersion = subjects.find(s =>
          s.versions?.some(v => v.id === chapter.subjectVersionId)
        );
        return subjectVersion?.id === filterSubjectId;
      });
    }

    // 版本过滤
    if (filterVersionId > 0) {
      filtered = filtered.filter(chapter =>
        chapter.subjectVersionId === filterVersionId
      );
    }

    // 年级学期过滤
    if (filterSchoolLevel) {
      filtered = filtered.filter(chapter => {
        const schoolLevel = getChapterSchoolLevel(chapter);
        return schoolLevel.includes(filterSchoolLevel);
      });
    }

    return filtered;
  }, [chapters, searchTerm, filterSubjectId, filterVersionId, filterSchoolLevel, subjects, getChapterSchoolLevel]);

  // 分页数据
  const paginatedChapters = useMemo(() => {
    const startIndex = page * rowsPerPage;
    return filteredChapters.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredChapters, page, rowsPerPage]);

  // 清除所有筛选条件
  const handleClearFilters = () => {
    setSearchTerm('');
    setFilterSubjectId(0);
    setFilterVersionId(0);
    setFilterSchoolLevel('');
    setPage(0);
  };

  // 处理分页变化
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // 当选择学科时加载版本
  useEffect(() => {
    if (selectedSubjectId > 0) {
      loadSubjectVersions(selectedSubjectId);
    } else {
      setSubjectVersions([]);
    }
  }, [selectedSubjectId]);

  // 打开新增对话框
  const handleAdd = () => {
    setEditingChapter(null);
    setFormData({ name: '', orderIndex: 1, subjectVersionId: 0 });
    setSelectedSubjectId(0);
    setOpenDialog(true);
  };

  // 打开编辑对话框
  const handleEdit = (chapter: Chapter) => {
    setEditingChapter(chapter);
    setFormData({
      name: chapter.name,
      orderIndex: chapter.orderIndex,
      subjectVersionId: chapter.subjectVersionId || 0,
    });
    
    // 找到对应的学科ID
    const subjectVersion = subjects.find(s => 
      s.versions?.some(v => v.id === chapter.subjectVersionId)
    );
    if (subjectVersion) {
      setSelectedSubjectId(subjectVersion.id);
    }
    
    setOpenDialog(true);
  };

  // 保存章节
  const handleSave = async () => {
    if (!formData.name.trim()) {
      setError('章节名称不能为空');
      return;
    }

    if (!formData.subjectVersionId) {
      setError('请选择学科版本');
      return;
    }

    try {
      if (editingChapter) {
        // 更新章节
        await curriculumAPI.updateChapter(editingChapter.id, {
          name: formData.name,
          orderIndex: formData.orderIndex,
        });
        setSuccess('章节更新成功');
      } else {
        // 创建新章节
        await curriculumAPI.createChapter(formData);
        setSuccess('章节创建成功');
      }

      setOpenDialog(false);
      await loadChapters();
    } catch (error: any) {
      console.error('保存章节失败:', error);
      setError('保存章节失败: ' + (error.message || '未知错误'));
    }
  };

  // 删除章节
  const handleDelete = async (chapter: Chapter) => {
    const confirmMessage = `⚠️ 警告：级联删除操作

确定要删除章节"${chapter.name}"吗？

此操作将同时删除：
• 该章节下的所有知识点
• 知识点关联的视频和图片文件

此操作不可撤销，请谨慎操作！`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      const response = await curriculumAPI.deleteChapter(chapter.id);
      if (response.deletedFilesCount > 0) {
        setSuccess(`章节删除成功，已同时删除 ${response.deletedFilesCount} 个文件`);
      } else {
        setSuccess('章节删除成功');
      }
      await loadChapters();
    } catch (error: any) {
      console.error('删除章节失败:', error);
      setError('删除章节失败: ' + (error.message || '未知错误'));
    }
  };

  // 获取章节的学科信息
  const getChapterSubjectInfo = (chapter: Chapter) => {
    // 首先尝试从章节DTO的字段获取（后端已经填充）
    if (chapter.subjectName && chapter.subjectVersionName) {
      return `${chapter.subjectName} - ${chapter.subjectVersionName}`;
    }

    // 如果没有，则从 subjects 中查找
    for (const subject of subjects) {
      const version = subject.versions?.find(v => v.id === chapter.subjectVersionId);
      if (version) {
        return `${subject.name} - ${version.name}`;
      }
    }
    return '未知学科';
  };



  return (
    <Layout>
      <Box sx={{ p: 3 }}>
        {/* 页面标题 */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <MenuBook sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h4" fontWeight="bold">
              章节管理
            </Typography>
          </Box>
          <Box>
            <Button
              startIcon={<Refresh />}
              onClick={loadChapters}
              disabled={loading}
              sx={{ mr: 1 }}
            >
              刷新
            </Button>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={handleAdd}
            >
              新增章节
            </Button>
          </Box>
        </Box>

        {/* 筛选和搜索区域 */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', mb: 2 }}>
              {/* 搜索框 */}
              <Box sx={{ minWidth: 200, flex: '1 1 auto' }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="搜索章节名称..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search />
                        </InputAdornment>
                      ),
                    }
                  }}
                />
              </Box>

              {/* 统一的科目版本筛选组件 */}
              <SubjectVersionFilter
                subjects={subjects}
                subjectVersions={[]} // 从subjects.versions中获取，不需要单独传递
                selectedSubjectId={filterSubjectId || ''}
                selectedVersionId={filterVersionId || ''}
                onSubjectChange={(subjectId) => {
                  setFilterSubjectId(subjectId as number || 0);
                  setFilterVersionId(0);
                }}
                onVersionChange={(versionId) => {
                  setFilterVersionId(versionId as number || 0);
                }}
                size="small"
              />

              {/* 每页显示数量 */}
              <Box sx={{ minWidth: 120 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>每页显示</InputLabel>
                  <Select
                    value={rowsPerPage}
                    label="每页显示"
                    onChange={(e) => {
                      setRowsPerPage(Number(e.target.value));
                      setPage(0);
                    }}
                  >
                    <MenuItem value={5}>5条</MenuItem>
                    <MenuItem value={10}>10条</MenuItem>
                    <MenuItem value={20}>20条</MenuItem>
                    <MenuItem value={50}>50条</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              {/* 清除筛选按钮 */}
              <Box>
                <Button
                  variant="outlined"
                  startIcon={<Clear />}
                  onClick={handleClearFilters}
                  size="small"
                >
                  清除
                </Button>
              </Box>
            </Box>

            {/* 筛选条件标签 */}
            <FilterChips
              subjects={subjects}
              subjectVersions={[]} // 从subjects.versions中获取
              searchTerm={searchTerm}
              selectedSubjectId={filterSubjectId || ''}
              selectedVersionId={filterVersionId || ''}
              otherFilters={[
                ...(filterSchoolLevel ? [{
                  key: 'schoolLevel',
                  label: '年级水平',
                  value: filterSchoolLevel,
                  displayValue: filterSchoolLevel,
                  onDelete: () => setFilterSchoolLevel('')
                }] : [])
              ]}
              onRemoveSearch={() => setSearchTerm('')}
              onRemoveSubject={() => {
                setFilterSubjectId(0);
                setFilterVersionId(0);
              }}
              onRemoveVersion={() => setFilterVersionId(0)}
              sx={{ mt: 1 }}
            />
            
            {/* 筛选结果统计 */}
            <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="body2" color="text.secondary">
                共找到 {filteredChapters.length} 个章节
              </Typography>
            </Box>
          </CardContent>
        </Card>

        {/* 章节列表 */}
        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>章节名称</TableCell>
                  <TableCell>年级学期</TableCell>
                  <TableCell>所属学科版本</TableCell>
                  <TableCell>排序</TableCell>
                  <TableCell>知识点数量</TableCell>
                  <TableCell>创建时间</TableCell>
                  <TableCell align="center">操作</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : filteredChapters.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <Typography color="text.secondary">
                        {searchTerm || filterSubjectId > 0 || filterVersionId > 0 || filterSchoolLevel
                          ? '没有找到符合条件的章节'
                          : '暂无章节数据'
                        }
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedChapters.map((chapter) => (
                    <TableRow key={chapter.id}>
                      <TableCell>{chapter.id}</TableCell>
                      <TableCell>
                        <Typography fontWeight="medium">
                          {chapter.name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getChapterSchoolLevel(chapter)}
                          size="small"
                          color="info"
                          variant="outlined"
                          sx={{ fontWeight: 'medium' }}
                        />
                      </TableCell>
                      <TableCell>
                        {getChapterSubjectInfo(chapter)}
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={chapter.orderIndex} 
                          size="small" 
                          color="primary" 
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={chapter.knowledgePoints?.length || 0} 
                          size="small" 
                          color="secondary" 
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        {chapter.createdAt ? new Date(chapter.createdAt).toLocaleString() : '-'}
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          onClick={() => handleEdit(chapter)}
                          color="primary"
                        >
                          <Edit />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(chapter)}
                          color="error"
                          disabled={!isSuperAdmin}
                          title={!isSuperAdmin ? "只有超级管理员可以删除章节" : "删除章节"}
                        >
                          <Delete />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* 分页组件 */}
          {filteredChapters.length > 0 && (
            <TablePagination
              component="div"
              count={filteredChapters.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[5, 10, 20, 50]}
              labelRowsPerPage="每页显示:"
              labelDisplayedRows={({ from, to, count }) =>
                `第 ${from}-${to} 条，共 ${count} 条`
              }
            />
          )}
        </Paper>

        {/* 新增/编辑对话框 */}
        <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle>
            {editingChapter ? '编辑章节' : '新增章节'}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2 }}>
              <TextField
                fullWidth
                label="章节名称 *"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                sx={{ mb: 2 }}
              />
              
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>选择学科 *</InputLabel>
                <Select
                  value={selectedSubjectId}
                  onChange={(e) => setSelectedSubjectId(Number(e.target.value))}
                  label="选择学科 *"
                >
                  <MenuItem value={0}>请选择学科</MenuItem>
                  {subjects.map((subject) => (
                    <MenuItem key={subject.id} value={subject.id}>
                      {subject.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>选择学科版本 *</InputLabel>
                <Select
                  value={formData.subjectVersionId}
                  onChange={(e) => setFormData({ ...formData, subjectVersionId: Number(e.target.value) })}
                  label="选择学科版本 *"
                  disabled={!selectedSubjectId}
                >
                  <MenuItem value={0}>请选择学科版本</MenuItem>
                  {subjectVersions.map((version) => (
                    <MenuItem key={version.id} value={version.id}>
                      {version.schoolLevel ? `${version.schoolLevel} - ${version.name}` : version.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                fullWidth
                label="排序序号"
                type="number"
                value={formData.orderIndex}
                onChange={(e) => setFormData({ ...formData, orderIndex: Number(e.target.value) })}
                inputProps={{ min: 1 }}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDialog(false)}>取消</Button>
            <Button onClick={handleSave} variant="contained">
              {editingChapter ? '更新' : '创建'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* 成功提示 */}
        <Snackbar
          open={!!success}
          autoHideDuration={3000}
          onClose={() => setSuccess('')}
        >
          <Alert severity="success" onClose={() => setSuccess('')}>
            {success}
          </Alert>
        </Snackbar>

        {/* 错误提示 */}
        <Snackbar
          open={!!error}
          autoHideDuration={5000}
          onClose={() => setError('')}
        >
          <Alert severity="error" onClose={() => setError('')}>
            {error}
          </Alert>
        </Snackbar>
      </Box>
    </Layout>
  );
};

export default ChapterManagePage;
